import { axiosInstance } from '@/Api/axiosV2Config';
import type {
  Society,
  SocietyFilters,
  SocietyListResponse,
  SocietyHierarchy,
  TreeNode,
  CreateSocietyDto,
  UpdateSocietyDto,
  AuditLog,
  User,
  RepairersResponse,
  CustomPricing,
} from '@/features/societies/types/society.types';

/**
 * API client partagé pour la gestion des sociétés
 * Contient les méthodes de lecture et d'écriture utilisées par admin et outil-pro
 */
export const societyApi = {
  // ==================== LECTURE ====================

  /**
   * Lister les sociétés avec filtres et pagination
   */
  getSocieties: async (filters?: SocietyFilters): Promise<SocietyListResponse> => {
    const { data } = await axiosInstance.get<SocietyListResponse>('/societies', {
      params: filters,
    });
    return data;
  },

  /**
   * Exporter les vendeurs en CSV (respecte les filtres affichés).
   * Réservé aux internes (droit `society.export`).
   */
  exportSocieties: async (filters?: SocietyFilters): Promise<Blob> => {
    const { data } = await axiosInstance.get('/societies/export', {
      params: filters,
      responseType: 'blob',
    });
    return data as Blob;
  },

  /**
   * Récupérer une société par ID
   */
  getSociety: async (id: string): Promise<Society> => {
    const { data } = await axiosInstance.get<Society>(`/societies/${id}`);
    return data;
  },

  /**
   * Récupérer les enfants directs uniquement
   */
  getSocietyChildren: async (id: string): Promise<Society[]> => {
    const { data } = await axiosInstance.get<Society[]>(`/societies/${id}/children`);
    return data;
  },

  getChildrenHaveStripe: async (id: string): Promise<{ hasChildWithStripe: boolean }> => {
    const { data } = await axiosInstance.get<{ hasChildWithStripe: boolean }>(`/societies/${id}/children-have-stripe`);
    return data;
  },

  /**
   * Récupérer les ancêtres d'une société
   */
  getSocietyAncestors: async (id: string): Promise<Society[]> => {
    const { data } = await axiosInstance.get<Society[]>(`/societies/${id}/ancestors`);
    return data;
  },

  /**
   * Récupérer les descendants d'une société
   */
  getSocietyDescendants: async (id: string): Promise<Society[]> => {
    const { data } = await axiosInstance.get<Society[]>(
      `/societies/${id}/descendants`,
    );
    return data;
  },

  /**
   * Récupérer la hiérarchie complète d'une société
   */
  getSocietyHierarchy: async (id: string): Promise<SocietyHierarchy> => {
    const { data } = await axiosInstance.get<SocietyHierarchy>(
      `/societies/${id}/hierarchy`,
    );
    return data;
  },

  /**
   * Récupérer l'arbre hiérarchique d'une société
   */
  getSocietyTree: async (id: string): Promise<TreeNode> => {
    const { data } = await axiosInstance.get<TreeNode>(`/societies/${id}/tree`);
    return data;
  },

  /**
   * Récupérer les utilisateurs d'une société
   */
  getSocietyUsers: async (id: string): Promise<User[]> => {
    const { data } = await axiosInstance.get<User[]>(`/societies/${id}/users`);
    return data;
  },

  /**
   * Récupérer les réparateurs d'une société
   */
  getSocietyRepairers: async (id: string): Promise<RepairersResponse> => {
    const { data } = await axiosInstance.get<RepairersResponse>(`/companies/${id}/repairers`);
    return data;
  },

  /**
   * Mettre à jour la configuration réparateurs d'une société
   */
  updateSocietyRepairers: async (id: string, updates: {
    workshopEnabled?: boolean;
    workshopAddress?: string;
    workshopLat?: number;
    workshopLng?: number;
    partnerGarageUuids?: string[];
  }): Promise<RepairersResponse> => {
    const { data } = await axiosInstance.patch<RepairersResponse>(`/societies/${id}/repairers`, updates);
    return data;
  },

  // ==================== AUDIT ====================

  /**
   * Récupérer les logs d'audit d'une société
   */
  getSocietyAuditLogs: async (id: string, limit = 50): Promise<AuditLog[]> => {
    const { data } = await axiosInstance.get<AuditLog[]>(`/societies/${id}/audit-logs`, {
      params: { limit },
    });
    return data;
  },

  /**
   * Récupérer les logs d'une société (alias avec limit plus élevé)
   */
  getSocietyLogs: async (id: string, limit = 100): Promise<AuditLog[]> => {
    const response = await axiosInstance.get<AuditLog[]>(`/societies/${id}/audit-logs`, {
      params: { limit },
    });
    return response.data;
  },

  // ==================== CRUD ====================

  /**
   * Créer une nouvelle société
   */
  createSociety: async (society: CreateSocietyDto): Promise<Society> => {
    const { data } = await axiosInstance.post<Society>('/societies', society);
    return data;
  },

  /**
   * Mettre à jour une société
   */
  updateSociety: async (id: string, society: UpdateSocietyDto): Promise<Society> => {
    const { data } = await axiosInstance.put<Society>(`/societies/${id}`, society);
    return data;
  },

  /**
   * Supprimer une société
   */
  deleteSociety: async (id: string): Promise<void> => {
    await axiosInstance.delete(`/societies/${id}`);
  },

  // ==================== ACTIONS ====================

  /**
   * Activer une société
   */
  activateSociety: async (id: string): Promise<Society> => {
    const { data } = await axiosInstance.put<Society>(`/societies/${id}/activate`);
    return data;
  },

  /**
   * Désactiver une société
   */
  deactivateSociety: async (id: string): Promise<Society> => {
    const { data } = await axiosInstance.put<Society>(`/societies/${id}/deactivate`);
    return data;
  },

  /**
   * Basculer le mode test
   */
  toggleTestMode: async (id: string, isTestMode: boolean): Promise<Society> => {
    const { data } = await axiosInstance.put<Society>(
      `/societies/${id}/test-mode`,
      { isTestMode },
    );
    return data;
  },

  // ==================== STRIPE ====================

  /**
   * Créer un compte Stripe pour une société
   */
  createStripeAccount: async (
    societyId: string,
    formData: FormData
  ): Promise<{
    success: boolean;
    account_id: string;
    society: { uuid: string; name: string; code: string };
  }> => {
    const { data } = await axiosInstance.post(`/stripe/create-vendor-for-society/${societyId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return data;
  },

  /**
   * Crée une Account Session pour le composant embedded `account-onboarding`.
   * Le `clientSecret` est consommé côté composant par `loadConnectAndInitialize`.
   */
  getStripeAccountSession: async (societyId: string): Promise<{
    success: boolean;
    clientSecret?: string;
    publishableKey?: string;
    error?: string;
  }> => {
    const { data } = await axiosInstance.post(`/stripe/account-session/${societyId}`);
    return data;
  },

  /**
   * Réconcilie l'état du compte Stripe avec la base (charges_enabled,
   * requirements, future_requirements, etc.). À appeler après onExit du
   * composant embedded pour ne pas attendre le webhook.
   */
  syncStripeAccountStatus: async (societyId: string): Promise<{
    success: boolean;
    status?: {
      stripeChargesEnabled: boolean;
      stripePayoutsEnabled: boolean;
      stripeDetailsSubmitted: boolean;
      stripeRequirementsDue: number;
      stripeFutureRequirementsDue: number;
      stripeDisabledReason: string | null;
      stripeCurrentDeadline: string | null;
      stripeLastSyncedAt: string;
    };
    error?: string;
  }> => {
    const { data } = await axiosInstance.post(`/stripe/sync-account-status/${societyId}`);
    return data;
  },

  // ==================== CONFIGURATION V2 ====================

  /**
   * Récupérer la configuration effective (merge groupe + propre)
   */
  getEffectiveConfiguration: async (id: string) => {
    return axiosInstance.get(`/societies/v2/${id}/configuration/effective`);
  },

  /**
   * Récupérer la configuration groupe
   */
  getGroupConfiguration: async (id: string) => {
    return axiosInstance.get(`/societies/v2/${id}/configuration/group`);
  },

  /**
   * Récupérer la configuration propre
   */
  getOwnConfiguration: async (id: string) => {
    return axiosInstance.get(`/societies/v2/${id}/configuration/own`);
  },

  /**
   * Mettre à jour la configuration groupe
   */
  updateGroupConfiguration: async (id: string, updates: any) => {
    return axiosInstance.patch(`/societies/v2/${id}/configuration/group`, updates);
  },

  /**
   * Mettre à jour la configuration propre
   */
  updateOwnConfiguration: async (id: string, updates: any) => {
    return axiosInstance.patch(`/societies/v2/${id}/configuration/own`, updates);
  },

  /**
   * Détacher une société de la configuration groupe
   */
  detachFromGroup: async (id: string) => {
    return axiosInstance.post(`/societies/v2/${id}/configuration/detach`);
  },

  /**
   * Définir l'autonomie de facturation d'un MEMBRE de groupe.
   * target : 'SELF' (facturée hors groupe) | 'INHERIT' (hérite du groupe) | UUID d'un ancêtre.
   * Écrit uniquement la config own du membre (jamais la config groupe partagée).
   */
  setBillingAutonomy: async (id: string, target: string) => {
    return axiosInstance.patch(`/societies/v2/${id}/billing-autonomy`, { target });
  },

  // ==================== CUSTOM PRICING ====================

  /**
   * Récupérer les custom pricings d'une société
   */
  getCustomPricings: async (id: string): Promise<CustomPricing[]> => {
    const { data } = await axiosInstance.get<CustomPricing[]>(`/societies/${id}/custom-pricings`);
    return data;
  },

  /**
   * Récupérer les prix catalogue pour une société
   */
  getCatalogPrices: async (id: string): Promise<{ duration: number; price: number }[]> => {
    const { data } = await axiosInstance.get<{ duration: number; price: number }[]>(`/societies/${id}/catalog-prices`);
    return data;
  },

  /**
   * Résoudre le servicePrice applicable pour une société et une durée.
   *
   * Priorités (single-level) :
   *   1. customPricing propre           (source: 'own')
   *   2. customPricing du parent billed (source: 'billed-parent')
   *   3. catalogue (society.createdAt)  (source: 'catalog')
   */
  resolveServicePrice: async (
    id: string,
    duration: number,
  ): Promise<{ price: number; source: 'own' | 'billed-parent' | 'catalog' }> => {
    const { data } = await axiosInstance.get<{
      price: number;
      source: 'own' | 'billed-parent' | 'catalog';
    }>(`/societies/${id}/service-price`, { params: { duration } });
    return data;
  },

  /**
   * Résoudre le servicePrice pour TOUTES les durées applicables (4 ou 7 selon
   * extendedDuration), en un seul round-trip.
   */
  getResolvedServicePrices: async (
    id: string,
  ): Promise<Array<{ duration: number; price: number; source: 'own' | 'billed-parent' | 'catalog' }>> => {
    const { data } = await axiosInstance.get<
      Array<{ duration: number; price: number; source: 'own' | 'billed-parent' | 'catalog' }>
    >(`/societies/${id}/service-prices`);
    return data;
  },

  /**
   * Historique complet des custom pricings (toutes les rows, actives et remplacées)
   */
  getCustomPricingsHistory: async (id: string): Promise<CustomPricing[]> => {
    const { data } = await axiosInstance.get<CustomPricing[]>(`/societies/${id}/custom-pricings/history`);
    return data;
  },

  /**
   * Preview de l'impact d'une nouvelle entrée tarifaire (avant création)
   */
  previewCreatePricingEntry: async (
    id: string,
    payload: { duration: number; effectiveDate: string },
  ): Promise<import('@/features/societies/types/society.types').CreatePricingPreview> => {
    const { data } = await axiosInstance.post(`/societies/${id}/custom-pricings/preview`, payload);
    return data;
  },

  /**
   * Crée une nouvelle entrée tarifaire (modèle historisé) + propage le prix.
   */
  createPricingEntry: async (
    id: string,
    payload: { duration: number; price: number; effectiveDate: string },
  ): Promise<import('@/features/societies/types/society.types').CreatePricingResult> => {
    const { data } = await axiosInstance.post(`/societies/${id}/custom-pricings`, payload);
    return data;
  },

  /**
   * Preview de la suppression d'une entrée tarifaire.
   */
  previewDeleteCustomPricing: async (
    id: number,
  ): Promise<import('@/features/societies/types/society.types').DeletePricingPreview> => {
    const { data } = await axiosInstance.get(`/custom-pricings/${id}/delete-preview`);
    return data;
  },

  /**
   * Supprimer une entrée tarifaire (recalcule les warranties/contrats si elle était active).
   */
  deleteCustomPricing: async (
    id: number,
  ): Promise<import('@/features/societies/types/society.types').DeletePricingResult> => {
    const { data } = await axiosInstance.delete(`/custom-pricings/${id}`);
    return data;
  },

  // ==================== DOCUMENTS ====================

  /**
   * Mettre à jour les documents (logo, kbis, rib, etc.)
   */
  updateDocuments: async (id: string, formData: FormData): Promise<Society> => {
    const { data } = await axiosInstance.patch<Society>(
      `/societies/${id}/documents`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return data;
  },
};
